package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.ControlHeight
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.noTransition
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.js.Date
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Russian's four plural categories, running rather than described.
 *
 * The band makes one claim in prose — that the last digit does not decide the form — and then hands
 * the reader the control that proves it. Step from 20 to 21 and the highlight jumps *backwards* to
 * `one`, because twenty-one items is singular; step to 11 and it does not, because the teens
 * override the unit digit. A paragraph asserting that is forgettable. A stepper that does it is not.
 *
 * **Frame zero already carries the argument.** [count] arrives at 21 from the page, so a visitor who
 * touches nothing still sees a singular Russian form beside the number 21, and `many` flagged as
 * untranslated. The interactivity is a second layer over a still image that already works.
 *
 * **The rules are not reimplemented here.** [russianPluralCategory] and [ProofKey] already carry
 * CLDR's Russian selection and the key this whole page follows; this band draws them at the pen's
 * proportions and nothing more. A second copy of a plural rule is a second copy to get wrong.
 *
 * State is the caller's, so the hero's instrument and this band can be driven from one count rather
 * than disagreeing about what the page is currently showing.
 */
@Composable
fun PluralsBand(count: Int, onCount: (Int) -> Unit) {
    Band(ground = BandGround.Paper, labelledBy = PLURALS_HEADING_ID) {
        // The band's own rhythm is Space.XXXL; label to heading is closer than that, so the two sit
        // in their own column rather than taking the band gap between them.
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            BandLabel(t("marketing.plurals_band.plural_checks", "Plural checks"))
            BandHeading(
                t(
                    "marketing.plurals_band.forms_for_each_language",
                    "The right forms for each language.",
                ),
                id = PLURALS_HEADING_ID,
                measure = 860,
            )
        }
        Div(PluralsGridStyle.toModifier().toAttrs()) {
            PluralsArgument()
            PluralsInstrument(count, onCount)
        }
    }
}

/** The prose half: the counter's concrete example and an explicit QA gate. */
@Composable
private fun PluralsArgument() {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        P(PluralsProseStyle.toModifier().toAttrs()) {
            Text(
                t(
                    "marketing.plurals_band.russian_counter_example",
                    "Russian uses the singular form for 21, but not 11. Try the counter to see the form change.",
                ),
            )
        }
        P(PluralsProseStyle.toModifier().toAttrs()) {
            Text(
                t(
                    "marketing.plurals_band.configured_plural_qa_gate",
                    "Lingvo checks required plural forms and placeholders. Add the QA command to CI to stop on configured errors.",
                ),
            )
        }
        // The pill is the sentence above turned into something you can paste. Accent on
        // accentSubtle measures 5.44:1, which is why the tint is a token rather than a wash of the
        // accent at some opacity over whatever ground it lands on.
        Div(PluralsCommandStyle.toModifier().toAttrs()) {
            Span(Modifier.color(palette.accent).toAttrs()) { Text(t("marketing.plurals_band.russian_qa_error_gate", "lingvo qa --locale ru --fail-on error")) }
        }
    }
}

/**
 * The instrument: one key, one locale, four categories and a count you can move.
 *
 * Nothing here calls the network or pretends to. The whole panel is the plural table for
 * [ProofKey.NAME] with the CLDR rule applied to the current count, which is exactly what the editor
 * shows and exactly what `lingvo qa` checks.
 */
@Composable
private fun PluralsInstrument(count: Int, onCount: (Int) -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    val selected = russianPluralCategory(count)
    val held = rememberHeldStepper(count)

    Div(PluralsInstrumentStyle.toModifier().toAttrs()) {
        // Header bar: the key on the left, the locale on the right, in the same frame grammar the
        // editor uses for a key's toolbar, so the two read as one product rather than two mock-ups.
        Row(
            PluralsBarStyle.toModifier(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Span(
                Modifier.monoFont().color(palette.textSecondary).fontSize(TypeScale.Micro).toAttrs(),
            ) { Text(ProofKey.NAME) }
            Span(
                Modifier
                    .monoFont()
                    .color(palette.accent)
                    .fontSize(TypeScale.Micro)
                    .fontWeight(700)
                    .letterSpacing(TypeScale.MicroTracking)
                    .toAttrs(),
            ) { Text("ru") }
        }

        Column(Modifier.fillMaxWidth().padding(Space.XL).gap(Space.LG)) {
            PluralsCounter(count, onCount)

            Ul(PluralsCategoryListStyle.toModifier().toAttrs { attr("role", "list") }) {
                ProofKey.RU_PLURALS.forEach { (category, form) ->
                    PluralsCategoryRow(
                        category = category,
                        form = form,
                        count = count,
                        selected = category == selected,
                        held = held,
                    )
                }
            }

            // The answer in words as well as in paint. A filled row is not a fact a screen reader
            // can read, and it is not a fact a colour-blind reader can read either.
            Div(
                Modifier
                    .monoFont()
                    .fillMaxWidth()
                    // textSecondary rather than the pen's lighter muted tone: at this size the line
                    // is normal text and owes 4.5:1, which the lighter tone does not clear on the
                    // instrument's sunken ground.
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.Small)
                    .toAttrs {
                        attr("aria-live", "polite")
                        attr("aria-atomic", "true")
                    },
            ) {
                val form = ProofKey.RU_PLURALS.firstOrNull { it.first == selected }?.second
                if (form == null) {
                    // The arrow chain is data — a count, the CLDR category it selects — and stays
                    // as it is. Only the verdict at the end of it is copy.
                    Text(
                        "$count → $selected → " +
                            t("marketing.plurals_band.not_translated_yet_qa_will_fail", "not translated yet · qa will fail"),
                    )
                } else {
                    Text("$count → $selected → \"${form.replace("{count}", count.toString())}\"")
                }
            }
        }
    }
}

/**
 * Minus, the number, plus.
 *
 * A stepper rather than a slider, for the reason this codebase already wrote down once: a range
 * input styled to match across engines is a great deal of CSS for a worse control, and this one is
 * keyboard-accessible with no invented ARIA at all.
 */
@Composable
private fun PluralsCounter(count: Int, onCount: (Int) -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier.fillMaxWidth().gap(Space.LG),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LButton(
            label = "−",
            onClick = { onCount((count - 1).coerceAtLeast(PLURALS_MIN_COUNT)) },
            modifier = PluralsStepperModifier,
            variant = LButtonVariant.SECONDARY,
            size = LButtonSize.MD,
            enabled = count > PLURALS_MIN_COUNT,
            accessibleLabel = tr("marketing.plurals_band.one_fewer_item", "One fewer item"),
        )
        Span(
            Modifier
                .displayFont()
                // Tabular figures, so 9 → 10 does not shove the buttons apart. The number is the
                // one thing in this panel that must never appear to move.
                .tabularNumbers()
                .color(palette.textPrimary)
                .fontSize(TypeScale.Hero)
                .fontWeight(700)
                .lineHeight(1.0)
                .letterSpacing((-1.5).px)
                .minWidth(COUNTER_WIDTH_PX.px)
                .styleModifier { property("text-align", "center") }
                .toAttrs(),
        ) { Text(count.toString()) }
        LButton(
            label = "+",
            onClick = { onCount((count + 1).coerceAtMost(PLURALS_MAX_COUNT)) },
            modifier = PluralsStepperModifier,
            variant = LButtonVariant.SECONDARY,
            size = LButtonSize.MD,
            enabled = count < PLURALS_MAX_COUNT,
            accessibleLabel = tr("marketing.plurals_band.one_more_item", "One more item"),
        )
    }
}

/**
 * One CLDR category.
 *
 * Rows are laid out once and never reflow: selection is expressed in paint alone, so moving between
 * categories cannot shift anything under the reader's eye. Three signals carry the state without
 * relying on colour — the fill, the label's weight, and the status line naming the category in
 * words.
 */
@Composable
private fun PluralsCategoryRow(
    category: String,
    form: String?,
    count: Int,
    selected: Boolean,
    held: Boolean,
) {
    val palette = ColorMode.current.toSitePalette()
    val missing = form == null
    Li(
        PluralsCategoryRowStyle.toModifier()
            .thenIf(selected) { Modifier.backgroundColor(palette.accent) }
            // The danger border survives selection. `many` is the category this key has not
            // translated, and the moment it becomes the selected form is precisely the moment that
            // fact matters most — an accent fill must not be allowed to swallow it.
            .thenIf(missing) {
                Modifier.styleModifier { property("border-color", palette.danger.toString()) }
            }
            .thenIf(held) { Modifier.noTransition() }
            .toAttrs(),
    ) {
        Span(
            Modifier
                .monoFont()
                .minWidth(CATEGORY_LABEL_WIDTH_PX.px)
                .color(
                    when {
                        selected -> palette.surfaceBase
                        missing -> palette.danger
                        else -> palette.textMuted
                    },
                )
                .fontSize(TypeScale.Micro)
                // A weight change is normally a reflow wearing a colour change's clothes. Here the
                // label is mono, so bold and regular have identical advance widths and the value
                // beside it cannot move.
                .fontWeight(if (selected) 700 else 400)
                .toAttrs(),
        ) { Text(category) }
        if (missing) {
            Span(
                Modifier
                    .color(if (selected) palette.surfaceBase else palette.danger)
                    .fontSize(TypeScale.BodySmall)
                    .toAttrs(),
            ) { Text(t("marketing.plurals_band.not_translated_yet", "not translated yet")) }
        } else {
            Span(
                Modifier
                    .color(if (selected) palette.surfaceBase else palette.textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    // Load-bearing: the Cyrillic face is chosen by :lang() and by nothing else.
                    .toAttrs { attr("lang", "ru") },
            ) { Text(form.orEmpty().replace("{count}", count.toString())) }
        }
    }
}

/**
 * True while the stepper is being held down, so the row can stop animating and simply be right.
 *
 * Holding `+` on a focused button repeats the click roughly every 30ms. A 180ms cross-fade under
 * that lands each step on a row that is still two thirds of the way through the *previous* step,
 * and the highlight smears behind the keyboard — the same failure as an animated caret, and the
 * same verdict: that is lag wearing polish's clothes.
 *
 * The test is the transition's own duration rather than an invented threshold. If a second step
 * arrives before the first has finished moving, the reader is outrunning the animation and the
 * animation should get out of the way; [Motion.StandardMs] of quiet hands it back. The measurement
 * lives in an effect rather than in composition so a recomposition for any other reason — a colour
 * mode change, a resize — cannot be mistaken for a keystroke.
 */
@Composable
private fun rememberHeldStepper(count: Int): Boolean {
    var held by remember { mutableStateOf(false) }
    val lastStepAt = remember { mutableStateOf(0.0) }
    LaunchedEffect(count) {
        val now = Date.now()
        // First composition compares against 0.0, which is 56 years of quiet: never "held".
        held = now - lastStepAt.value < Motion.StandardMs
        lastStepAt.value = now
        delay(Motion.StandardMs.toLong())
        held = false
    }
    return held
}

private const val PLURALS_HEADING_ID = "plurals-title"

/** 0 is a real Russian count and it selects `many` — 0 товаров. It is a fine floor. */
private const val PLURALS_MIN_COUNT = 0

/** Three digits is as far as the lesson goes; 111 already proves the teens rule. */
private const val PLURALS_MAX_COUNT = 999

/**
 * Geometry, not spacing: the number's box is wide enough for three digits at [TypeScale.Hero] so
 * the two buttons hold still while the count changes.
 */
private const val COUNTER_WIDTH_PX = 96

/** Geometry: the category column, wide enough for `other` and identical on every row. */
private const val CATEGORY_LABEL_WIDTH_PX = 48

/**
 * Prose and instrument, side by side above [Breakpoint.LG] and stacked below it.
 *
 * The instrument is a fixed column rather than a fraction: it holds a 3-digit number at
 * [TypeScale.Hero] between two 40px buttons and four rows of Russian, and letting that reflow with
 * the viewport buys nothing and costs the alignment of the rows.
 */
val PluralsGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.Huge)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("align-items", "start")
            }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("grid-template-columns", "minmax(0, 1fr) ${INSTRUMENT_WIDTH_PX}px") }
    }
}

/** Geometry: the instrument's column, sized to its contents rather than to the viewport. */
private const val INSTRUMENT_WIDTH_PX = 440

val PluralsProseStyle = CssStyle.base {
    Modifier
        // A reading measure, in the only unit that expresses one honestly: roughly 65 characters at
        // TypeScale.Body, which is where a line stops being comfortable.
        .maxWidth(460.px)
        .margin(0.px)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Body)
        .lineHeight(TypeScale.BodyLineHeight)
}

val PluralsCommandStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Small)
        .backgroundColor(colorMode.toSitePalette().accentSubtle)
        .borderRadius(Radius.Pill)
        .padding(topBottom = Space.SM, leftRight = Space.LG)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            // In a flex column a child stretches by default; a pill that spans the measure is a bar.
            property("align-self", "flex-start")
        }
}

val PluralsInstrumentStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .borderRadius(Radius.Modal)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("overflow", "hidden")
        }
}

val PluralsBarStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = Space.LG, leftRight = Space.XL)
        .styleModifier {
            property("justify-content", "space-between")
            property("border-bottom", "1px solid ${colorMode.toSitePalette().border}")
        }
}

val PluralsCategoryListStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.SM)
        .margin(0.px)
        .padding(0.px)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            // Markers removed, so the role is restored explicitly at the call site — Safari drops
            // list semantics along with the bullets.
            property("list-style", "none")
        }
}

/**
 * A category row. Colour is the only thing that moves.
 *
 * [Motion.StandardMs] rather than the 120ms a hover tint would take, because this is not a hover:
 * it is the answer to a press, and the eye should be able to follow the highlight from the row it
 * left to the row it landed on. Nothing translates, nothing fades, and the row's box is identical
 * in every state, so a selection change can never reflow the panel.
 */
val PluralsCategoryRowStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.LG)
        .padding(topBottom = Space.MD, leftRight = Space.LG)
        .borderRadius(Radius.Control)
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .transitionColors(durationMs = Motion.StandardMs)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("border", "1px solid ${colorMode.toSitePalette().border}")
        }
}

/**
 * The stepper buttons, at the pen's proportions.
 *
 * A pill and a 40px square rather than [LButtonSize.MD]'s default box: the two read as one control
 * with the number between them, and a rounded rectangle beside a 52px figure reads as a form field.
 * The press, the focus ring and the disabled state all still come from [LButton].
 *
 * A plain `Modifier` rather than a `CssStyle`, and that is not a preference. [LButton] applies its
 * size as inline properties and then chains the caller's modifier onto the same chain, so an
 * inline override lands last and wins. A class-based style would tie with `LButtonStyle` on
 * specificity and be decided by stylesheet order — which is to say, by nothing legible.
 */
private val PluralsStepperModifier: Modifier = Modifier
    // A circle at [ControlHeight.MD], the smallest control this system draws and the minimum
    // comfortable target, which is also the number's line box. Written as two raw `40.px` before,
    // which is the one place in this file the ladder's own token was available and unused.
    .width(ControlHeight.MD)
    .height(ControlHeight.MD)
    .padding(0.px)
    .borderRadius(Radius.Pill)
    .fontSize(TypeScale.HeadingSmall)
    .fontWeight(400)
    .lineHeight(1.0)
